import { useEffect, useRef, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button, Card, Checkbox, message, Select, Table, TableProps, Tag } from 'antd';
import { useAccount, useAvailableStore } from 'views/Assistant/hooks';
import Empty from 'components/Empty';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { buyerNickMask } from 'services/channels/demo';
import dayjs from 'lib/dayjs';
import { LoadingOutlined } from '@ant-design/icons';
import { getRemindFromHint } from 'views/Conversation/Chat/components/Remind';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useFilterForm } from './FilterForm';
import useScene from '../hooks/useScene';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: normal;
  h3 {
    margin: 0 16px 0 0;
    font-size: 16px;
    font-weight: 500;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  .${({ theme }) => theme.getAntdPrefixedClassName('select')} {
    width: 260px;
    margin-left: 24px;
  }
`;

type TR = Remind.DetailRecord;

export interface DashboardDetailProps extends TableProps<TR> {
  onViewConversation: (record: TR) => void;
}

function DashboardDetail(props: DashboardDetailProps) {
  const { dataSource, onViewConversation, ...otherProps } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const systemLanguage = useSystemLanguageValue();

  const { config: storeNameConfig } = useStoreNameConfig();

  const {
    values: { platformRegion, remindStatus, storeId, assistantId },
    dirty,
    isSubmitting,
    setValues,
    handleSubmit,
  } = useFilterForm();

  const isAllPlatformRegion = platformRegion.every(({ region }) => region.length === 0);

  const {
    loading: storeListLoading,
    list: storeList,
    fetchList: fetchStoreList,
  } = useAvailableStore();

  const storeMap = useMemo(() => {
    return (storeList || []).reduce<Record<string, Assistant.GroupAvailableStore>>((res, store) => {
      const { storeId } = store;

      res[storeId] = store;

      return res;
    }, {});
  }, [storeList]);

  const {
    loading: accountListLoading,
    list: accountList,
    fetchList: fetchAccountList,
  } = useAccount();

  const { loading: sceneListLoading, list: sceneList, fetchList: fetchSceneList } = useScene();

  const sceneMap = useMemo(() => {
    return (sceneList || []).reduce<Record<string, Remind.SceneInfo>>((res, cur) => {
      const { scene } = cur;

      res[scene] = cur;

      return res;
    }, {});
  }, [sceneList]);

  const refsCurrent = {
    fetchStoreList,
    fetchAccountList,
    fetchSceneList,
    t,
    dirty,
    handleSubmit,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const onSubmit = () => {
    setTimeout(() => {
      const {
        current: { dirty, handleSubmit },
      } = refs;

      if (dirty) {
        handleSubmit();
      }
    });
  };

  useEffect(() => {
    const {
      current: { fetchStoreList, fetchAccountList, fetchSceneList, t },
    } = refs;

    fetchStoreList().catch(() => {
      message.error(t('remind.网络错误，请稍后再试'));
    });

    fetchAccountList({
      page: 1,
      pageSize: 9999999,
    }).catch(() => {
      message.error(t('remind.网络错误，请稍后再试'));
    });

    fetchSceneList().catch(() => {
      message.error(t('remind.网络错误，请稍后再试'));
    });
  }, []);

  return (
    <Card
      title={
        <Header>
          <HeaderLeft>
            <h3>{t('remind.站内提醒详情')}</h3>
            <Checkbox
              checked={remindStatus === 'UNSOLVED'}
              disabled={isSubmitting}
              onChange={({ target: { checked } }) => {
                setValues((values) => {
                  return {
                    ...values,
                    remindStatus: checked ? 'UNSOLVED' : undefined,
                  };
                });

                onSubmit();
              }}
            >
              {t('remind.仅看未解决的会话')}
            </Checkbox>
          </HeaderLeft>
          <HeaderRight>
            <Select
              placeholder={t('remind.选择店铺')}
              mode="multiple"
              loading={storeListLoading}
              disabled={isSubmitting}
              allowClear
              showArrow
              maxTagCount="responsive"
              value={storeId}
              onChange={(storeId) =>
                setValues((values) => {
                  return {
                    ...values,
                    storeId,
                  };
                })
              }
              onDropdownVisibleChange={onSubmit}
              onClear={onSubmit}
              options={(storeList || []).reduce<{ value: string; label: string }[]>(
                (res, store) => {
                  const { storeId, storeName, alias, externalType, region } = store;

                  if (
                    !isAllPlatformRegion &&
                    platformRegion.every(
                      (item) => item.platform !== externalType || !item.region.includes(region),
                    )
                  ) {
                    return res;
                  }

                  const storeAliasFirst = isStoreAliasFirst(storeNameConfig, externalType, region);

                  res.push({
                    value: storeId,
                    label: storeAliasFirst ? alias || storeName : storeName,
                  });

                  return res;
                },
                [],
              )}
              optionFilterProp="label"
              tagRender={(props) => {
                const { onClose, label, ...otherProps } = props;

                return (
                  <Tag
                    onClose={(e) => {
                      onClose(e);
                      onSubmit();
                    }}
                    {...otherProps}
                  >
                    {label}
                  </Tag>
                );
              }}
            />
            <Select
              placeholder={t('remind.选择客服')}
              mode="multiple"
              loading={accountListLoading}
              disabled={isSubmitting}
              allowClear
              showArrow
              maxTagCount="responsive"
              value={assistantId}
              onChange={(assistantId) =>
                setValues((values) => {
                  return {
                    ...values,
                    assistantId,
                  };
                })
              }
              onDropdownVisibleChange={onSubmit}
              onClear={onSubmit}
              options={(accountList || []).map((account) => {
                const { id, name } = account;

                return {
                  value: id,
                  label: name,
                };
              })}
              optionFilterProp="label"
              tagRender={(props) => {
                const { onClose, label, ...otherProps } = props;

                return (
                  <Tag
                    onClose={(e) => {
                      onClose(e);
                      onSubmit();
                    }}
                    {...otherProps}
                  >
                    {label}
                  </Tag>
                );
              }}
            />
          </HeaderRight>
        </Header>
      }
    >
      {!dataSource || dataSource.length === 0 ? (
        <Empty />
      ) : (
        <Table
          dataSource={dataSource}
          rowKey={({ buyerId, remindTime }) => `${buyerId}@${remindTime}`}
          columns={[
            {
              key: 'platform',
              title: t('remind.所属平台'),
              render: (_, record: TR) => {
                const { platform, region } = record;

                return (
                  <>
                    <div>{platformLabelMap[toLowerCase(platform)]}</div>
                    <div>{getRegionLabel(region)}</div>
                  </>
                );
              },
            },
            {
              key: 'storeId',
              dataIndex: 'storeId',
              title: t('remind.店铺名称'),
              render: (storeId: TR['storeId'], record: TR) => {
                const { [storeId]: store } = storeMap;

                if (!store) {
                  return record.storeName;
                }

                const { storeName, alias, externalType, region } = store;

                const storeAliasFirst = isStoreAliasFirst(storeNameConfig, externalType, region);

                return storeAliasFirst ? alias || storeName : storeName;
              },
            },
            {
              key: 'buyerNick',
              dataIndex: 'buyerNick',
              title: t('remind.买家昵称'),
              render: buyerNickMask,
            },
            {
              key: 'remindTime',
              dataIndex: 'remindTime',
              title: t('remind.提醒时间'),
              render: (time: TR['remindTime']) =>
                dayjs(parseInt(time, 10)).format('YYYY-MM-DD HH:mm:ss'),
            },
            {
              key: 'scene',
              dataIndex: 'scene',
              title: t('remind.提醒类型'),
              render: (scene: TR['scene']) => {
                if (sceneListLoading) {
                  return <LoadingOutlined />;
                }

                const { [scene]: sceneInfo } = sceneMap;

                if (!sceneInfo) {
                  return '-';
                }

                return getRemindFromHint(sceneInfo.sceneName, systemLanguage);
              },
            },
            {
              key: 'solveAssistantName',
              dataIndex: 'solveAssistantName',
              title: t('remind.解决客服'),
              render: (name: TR['solveAssistantName']) => name || '-',
            },
            {
              key: 'elapsedTime',
              dataIndex: 'elapsedTime',
              title: t('remind.解决耗时'),
              render: (time: TR['elapsedTime']) => {
                const milliseconds = parseInt(time, 10);

                if (!milliseconds) {
                  return '-';
                }

                const minutes = Math.ceil(milliseconds / 1000 / 60);

                const hour = Math.floor(minutes / 60);

                const restMinutes = minutes % 60;

                return `${hour ? `${hour}h` : ''}${restMinutes ? `${restMinutes}min` : ''}`;
              },
            },
            {
              key: 'outSiteRemind',
              dataIndex: 'outSiteRemind',
              title: t('remind.是否站外提醒'),
              render: (remind: TR['outSiteRemind']) => (remind ? t('remind.是') : t('remind.否')),
            },
            {
              key: 'operation',
              dataIndex: 'operation',
              title: t('操作'),
              render: (_, record: TR) => {
                return (
                  <Button type="link" onClick={() => onViewConversation(record)}>
                    {t('remind.查看会话')}
                  </Button>
                );
              },
            },
          ]}
          {...otherProps}
        />
      )}
    </Card>
  );
}

export default DashboardDetail;
